/**
 * useCreditBalance Hook
 *
 * React hook for managing credit balance state with real-time updates.
 *
 * ⚠️ Frontend never deducts credits. Backend is the single source of truth.
 * This hook only provides read-only access to credit balance.
 */

import { useState, useEffect, useCallback } from "react";
import {
  getCreditBalance,
  getCreditBalances,
  checkSufficientCredits,
  subscribeToCreditBalance,
  creditBalanceEvent,
} from "@/services/creditService";
import { JobType, getCreditCost } from "@/types/credits";

interface UseCreditBalanceResult {
  /** Current credit balance */
  /** Total available credits (regular + bonus) */
  balance: number;
  /** Regular credits balance */
  regular: number;
  /** Bonus credits balance */
  bonus: number;
  /** Whether balance is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh the balance manually */
  refresh: () => Promise<void>;
  /** Check if user has sufficient credits for a job type */
  hasSufficientCredits: (jobType: JobType) => boolean;
  /** Get the shortfall for a job type (0 if sufficient) */
  getShortfall: (jobType: JobType) => number;
}

/**
 * Hook to manage credit balance with real-time updates
 */
export function useCreditBalance(
  userId: string | undefined,
): UseCreditBalanceResult {
  const [balance, setBalance] = useState<number>(0);
  const [regular, setRegular] = useState<number>(0);
  const [bonus, setBonus] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch initial balance
  useEffect(() => {
    if (!userId) {
      setBalance(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getCreditBalances(userId)
      .then(({ regular: r, bonus: b }) => {
        setRegular(r);
        setBonus(b);
        setBalance((r ?? 0) + (b ?? 0));
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching credit balance:", err);
        setError("Failed to fetch credit balance");
        setLoading(false);
      });
  }, [userId]);

  // Subscribe to real-time balance updates
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToCreditBalance(userId, (newBalances) => {
      setRegular(newBalances.regular ?? 0);
      setBonus(newBalances.bonus ?? 0);
      setBalance((newBalances.regular ?? 0) + (newBalances.bonus ?? 0));
    });

    return unsubscribe;
  }, [userId]);

  // Manual refresh function
  const refresh = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const { regular: r, bonus: b } = await getCreditBalances(userId);
      setRegular(r);
      setBonus(b);
      setBalance((r ?? 0) + (b ?? 0));
    } catch (err) {
      console.error("Error refreshing credit balance:", err);
      setError("Failed to refresh credit balance");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Listen for immediate client-side balance broadcasts (e.g. after consume)
  useEffect(() => {
    if (!userId) return;
    const handler = (e: Event) => {
      try {
        // CustomEvent with detail = new balance
        const ce = e as CustomEvent<any>;
        if (typeof ce.detail === "number") {
          // Detail is total new balance - refresh to get breakdown
          refresh();
        } else if (ce.detail && typeof ce.detail === "object") {
          const d = ce.detail as { regular?: number; bonus?: number };
          if (typeof d.regular === "number") setRegular(d.regular);
          if (typeof d.bonus === "number") setBonus(d.bonus);
          if (typeof d.regular === "number" || typeof d.bonus === "number") {
            setBalance((d.regular ?? regular) + (d.bonus ?? bonus));
          }
        }
      } catch (err) {
        // ignore
      }
    };

    creditBalanceEvent.addEventListener("updated", handler as EventListener);
    return () =>
      creditBalanceEvent.removeEventListener(
        "updated",
        handler as EventListener,
      );
  }, [userId]);

  // Check if user has sufficient credits
  const hasSufficientCredits = useCallback(
    (jobType: JobType): boolean => {
      const required = getCreditCost(jobType);
      return balance >= required;
    },
    [balance],
  );

  // Get shortfall for a job type
  const getShortfall = useCallback(
    (jobType: JobType): number => {
      const required = getCreditCost(jobType);
      return Math.max(0, required - balance);
    },
    [balance],
  );

  return {
    balance,
    regular,
    bonus,
    loading,
    error,
    refresh,
    hasSufficientCredits,
    getShortfall,
  };
}

export default useCreditBalance;
